import type { ConsultationRow } from '@/pages/admin/types';
import { levelOf } from '@/pages/admin/utils/level';
import { symptomCategories } from '@/mocks/questionnaire';
import { fieldText, formatTime } from '@/pages/admin/utils/fields';

interface Column {
  header: string;
  get: (row: ConsultationRow) => string;
}

const text = (name: string) => (row: ConsultationRow) =>
  fieldText(row as unknown as Record<string, unknown>, name);

const columns: Column[] = [
  { header: '提交时间', get: (row) => formatTime(row.created_at) },
  { header: '评估等级', get: (row) => levelOf(row).shortLabel },
  { header: '总分', get: (row) => String(row.total_score ?? 0) },
  { header: '已评项数', get: (row) => String(row.rated_count ?? 0) },
  { header: '中度以上项数', get: (row) => String(row.severe_count ?? 0) },
  { header: '重点关注信号', get: (row) => row.focus_summary || '' },
  { header: '联系方式', get: (row) => row.contact || '' },
  { header: '年龄', get: text('age') },
  { header: '身高体重', get: text('body') },
  { header: '月经状态', get: text('menstrual') },
  { header: '最近一次月经', get: text('last_period') },
  { header: '周期变化', get: text('cycle_change') },
  { header: '经量变化', get: text('flow_change') },
  { header: '月经颜色性状', get: text('period_color') },
  { header: '体检时间', get: text('exam_date') },
  { header: 'FSH', get: (row) => row.fsh || '' },
  { header: 'E2', get: (row) => row.e2 || '' },
  { header: '血脂血糖', get: text('lipid') },
  { header: '骨密度', get: text('bone') },
  { header: '乳腺检查', get: text('breast') },
  { header: '妇科B超', get: text('uterus') },
  { header: '甲状腺功能', get: text('thyroid') },
  { header: '既往病史', get: text('history') },
  { header: '手术史', get: text('surgery') },
  { header: '激素补充治疗', get: text('hrt') },
  { header: '正在服用', get: (row) => row.meds || '' },
  { header: '过敏史', get: (row) => row.allergy || '' },
  { header: '家族病史', get: text('family') },
];

symptomCategories.forEach((category) => {
  category.items.forEach((item) => {
    columns.push({ header: `症状·${item.label}`, get: text(item.name) });
  });
});

columns.push(
  { header: '最困扰的症状', get: (row) => row.top_symptoms || '' },
  { header: '希望得到的帮助', get: text('needs') },
  { header: '补充说明', get: (row) => row.concern || '' },
);

const escapeCell = (value: string): string => {
  const safe = value.replace(/"/g, '""').replace(/\r?\n/g, ' ');
  return `"${safe}"`;
};

export function buildCsv(rows: ConsultationRow[]): string {
  const head = columns.map((column) => escapeCell(column.header)).join(',');
  const body = rows.map((row) =>
    columns.map((column) => escapeCell(column.get(row) || '')).join(','),
  );
  return [head, ...body].join('\r\n');
}

export function exportConsultations(rows: ConsultationRow[], prefix = '问诊记录'): void {
  if (!rows.length) return;
  const csv = buildCsv(rows);
  const blob = new Blob([`\uFEFF${csv}`], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const now = new Date();
  const pad = (n: number) => String(n).padStart(2, '0');
  const stamp = `${now.getFullYear()}${pad(now.getMonth() + 1)}${pad(now.getDate())}-${pad(now.getHours())}${pad(now.getMinutes())}`;
  const link = document.createElement('a');
  link.href = url;
  link.download = `${prefix}-${stamp}.csv`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}